'use client';

import { EMAIL_INPUT_PATTERN, formatEmail } from '@/components/Form/EmailInput';
import Image from '@/components/Image';
import { useJump } from '@/components/MagicJump';
import SubscribeConsentDialog from '@/components/Subscribe/SubscribeConsentDialog';
import { useSubscribeExposureTracking } from '@/components/Subscribe/useSubscribeExposureTracking';
import { toast } from '@/components/Toast';
import { useRouteCnName } from '@/hooks/useRouteMetadata';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { env } from '@/utils/env';
import { sensorsTrack } from '@/utils/sensors';
import { relationEmailMaxLength } from '@/utils/tools';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import appStoreBadge from './icon/app-store-badge.png';
import googlePlayBadge from './icon/google-play-badge.png';

const SUBSCRIBE_TITLE = 'Missed a deal? Subscribe to never miss a discount again.';

const Subscribe2026 = () => {
  const token = useStore((state) => state.token);
  const storeAccountEmail = useStore((state) => state.userInfo?.email || '');
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const getMarketingStatus = useStore((state) => state.getMarketingStatus);
  const setLoginRegisterShow = useStore((state) => state.setShowLoginRegisterDialog);
  const t = useTranslations('layout.footer');
  const tEmailValidation = useTranslations('components.form.email.validation');
  const toJumpTarget = useJump();
  const pathname = usePathname();
  const pageName = useRouteCnName();
  const [email, setEmail] = useState('');
  const [consentDialogVisible, setConsentDialogVisible] = useState(false);
  const isLoggedIn = Boolean(token);

  useEffect(() => {
    if (storeAccountEmail) {
      setEmail(storeAccountEmail);
    } else if (!token) {
      setEmail('');
    }
  }, [storeAccountEmail, token]);

  useEffect(() => {
    if (!token || storeAccountEmail) return;

    refreshAccountInfo();
  }, [refreshAccountInfo, storeAccountEmail, token]);

  const trackEDMModuleExp = () => {
    sensorsTrack('EDMModule_Exp', {
      page_name: pageName,
    });
  };

  const trackEDMModuleClick = () => {
    sensorsTrack('EDMModule_Click', {
      page_name: pageName,
      btn_name: t('subscribe.subscribeButton'),
    });
  };

  const { ref: exposureRef } = useSubscribeExposureTracking({
    callback: trackEDMModuleExp,
    pathname,
  });

  const handleOnTabSubscribe = async () => {
    if (isLoggedIn) {
      const currentMarketingStatus = await getMarketingStatus();
      if (!currentMarketingStatus?.marketing_granted?.email) {
        setConsentDialogVisible(true);
        return;
      }

      toJumpTarget({
        Jump_target: `/account/my-account`,
        jump_type: 4,
      });
      return;
    }

    const submitEmail = email.trim();
    if (!EMAIL_INPUT_PATTERN.test(submitEmail)) {
      toast.error(tEmailValidation('invalid'));
      return;
    }

    setEmail(submitEmail);
    setLoginRegisterShow({
      show: true,
      initialEmail: submitEmail,
    });
  };

  const handleSubscribeButtonClick = () => {
    trackEDMModuleClick();
    void handleOnTabSubscribe();
  };

  return (
    <>
      <section
        ref={exposureRef}
        className="flex w-full items-center justify-between gap-[16px] bg-black px-[72px] py-[40px] text-primary-white"
        aria-label={SUBSCRIBE_TITLE}
      >
        {env.NEXT_PUBLIC_HAS_APP === 'true' ? (
          <div className="flex w-[560px] shrink-0 flex-col items-start gap-[12px]">
            <h2 className="whitespace-nowrap font-bb text-[20px] leading-[24px]">
              {t('downloadApp.title')} {t('downloadApp.saveMore')}
            </h2>
            <div className="flex items-center gap-[12px]">
              <div className="group relative h-[32px] w-[124px] cursor-pointer">
                <Image
                  src={appStoreBadge}
                  alt={t('downloadApp.appStore')}
                  className="h-full w-full"
                  width={124}
                  height={32}
                />
                <Image
                  src="/home-page/app-download-qrcode.png"
                  alt=""
                  className="absolute bottom-[44px] left-0 hidden size-[124px] group-hover:block"
                  width={124}
                  height={124}
                />
              </div>
              <div className="group relative h-[32px] w-[124px] cursor-pointer">
                <Image
                  src={googlePlayBadge}
                  alt={t('downloadApp.googlePlay')}
                  className="h-full w-full"
                  width={124}
                  height={32}
                />
                <Image
                  src="/home-page/app-download-qrcode.png"
                  alt=""
                  className="absolute bottom-[44px] left-0 hidden size-[124px] group-hover:block"
                  width={124}
                  height={124}
                />
              </div>
            </div>
          </div>
        ) : null}

        <div className="ml-auto flex w-[560px] shrink-0 flex-col items-start gap-[12px]">
          <h2 className="whitespace-nowrap font-bb text-[20px] leading-[24px]">
            {SUBSCRIBE_TITLE}
          </h2>
          <div className="flex h-[50px] w-full items-center gap-[8px] rounded-[6px] border border-[#d1d9e0] bg-primary-white p-[6px] pl-[12px]">
            <input
              type="email"
              maxLength={50 + relationEmailMaxLength}
              value={email}
              disabled={isLoggedIn}
              onChange={(event) => setEmail(formatEmail(event.target.value))}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleOnTabSubscribe();
                }
              }}
              aria-label={t('subscribe.emailPlaceholder')}
              placeholder={t('subscribe.emailPlaceholder')}
              className="h-full min-w-0 flex-1 bg-transparent font-br text-[14px] leading-[17px] text-[#282828] outline-none placeholder:text-[#6c6c6c] disabled:cursor-not-allowed disabled:text-[#6c6c6c] disabled:opacity-100"
            />
            <button
              type="button"
              className="h-[38px] w-[96px] shrink-0 cursor-pointer rounded-[4px] bg-primary-purple text-center font-br text-[12px] leading-[14px] text-primary-white"
              onClick={handleSubscribeButtonClick}
            >
              {t('subscribe.subscribeButton')}
            </button>
          </div>
        </div>
      </section>

      <SubscribeConsentDialog open={consentDialogVisible} onOpenChange={setConsentDialogVisible} />
    </>
  );
};

export default Subscribe2026;
